import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PresignedAvatar from '../../../components/common/PresignedAvatar';
import {
  pollPercent,
  resolvePollConfig,
  selectedPollOptions,
  tallyPollVotes,
} from '../utils/pollUtils';

const displayName = (person, directory) => {
  if (person?.userName && person.userName !== 'User') return person.userName;
  const user = directory.get(String(person?.userId));
  if (!user) return person?.userName || 'Someone';
  const full = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return full || user.username || person?.userName || 'Someone';
};

const PollVoters = ({ voters = [], directory }) => {
  if (!voters.length) {
    return <p className="mt-2 text-[11px] text-gray-400">No votes yet</p>;
  }
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {voters.map((person) => {
        const user = directory.get(String(person?.userId));
        const name = displayName(person, directory);
        return (
          <span
            key={person?.userId || name}
            className="inline-flex items-center gap-1.5 max-w-full rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 pl-0.5 pr-2 py-0.5"
            title={name}
          >
            <PresignedAvatar
              fileKey={person?.profilePicture || user?.profilePicture}
              cacheKey={user?.username || person?.userId}
              name={name}
              initials={name.charAt(0)}
              className="w-5 h-5 rounded-full object-cover"
              fallbackClassName="flex items-center justify-center bg-gradient-to-br from-sky-400 to-violet-500 text-white text-[9px] font-bold"
            />
            <span className="text-[11px] font-medium text-gray-700 dark:text-gray-200 truncate max-w-[9rem]">
              {name}
            </span>
          </span>
        );
      })}
    </div>
  );
};

const PollVoteBlock = ({
  eventInfo,
  currentUserId,
  canVote = false,
  saving = false,
  onVote,
  compact = false,
  showVoters = false,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(Boolean(showVoters) && !compact);
  const directoryUsers = useSelector((state) => state.users?.users || []);
  const directory = useMemo(
    () => new Map((directoryUsers || []).map((user) => [String(user.id), user])),
    [directoryUsers],
  );
  const { question, options, allowMultiple } = resolvePollConfig(eventInfo);
  const participants = eventInfo?.participants || [];
  const { counts, voters, voterCount } = tallyPollVotes(options, participants, eventInfo);
  const mine = participants.find((p) => String(p.userId) === String(currentUserId));
  const myVotes = selectedPollOptions(mine, eventInfo).length
    ? selectedPollOptions(mine, eventInfo)
    : selectedPollOptions({ userId: currentUserId }, eventInfo);
  const closed = ['CLOSED', 'CANCELLED', 'EXPIRED'].includes(String(eventInfo?.status || '').toUpperCase());
  const interactive = canVote && !closed && !saving && typeof onVote === 'function';
  const revealVoters = showVoters || detailsOpen;

  if (!options.length) return null;

  const toggle = (option) => {
    if (!interactive) return;
    let next;
    if (allowMultiple) {
      next = myVotes.includes(option)
        ? myVotes.filter((item) => item !== option)
        : [...myVotes, option];
    } else {
      next = myVotes.includes(option) ? [] : [option];
    }
    if (next.length === myVotes.length && next.every((item) => myVotes.includes(item))) return;
    onVote(next);
  };

  return (
    <div
      className={compact ? 'space-y-2' : 'space-y-3'}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {question && question !== eventInfo?.name && (
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{question}</p>
      )}
      <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
        {closed
          ? 'Closed'
          : interactive
            ? (allowMultiple ? 'Select one or more · tap again to unvote' : 'Select one · tap again to unvote')
            : (allowMultiple ? 'Multiple choice' : 'Single choice')}
      </p>
      <div className="space-y-2">
        {options.map((option) => {
          const checked = myVotes.includes(option);
          const count = counts[option] || 0;
          const percent = pollPercent(count, voterCount);
          const people = voters[option] || [];
          return (
            <div
              key={option}
              className={`w-full text-left rounded-xl border px-3 py-2.5 ${
                checked
                  ? 'border-emerald-400 bg-emerald-50/80 dark:border-emerald-700 dark:bg-emerald-950/30'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
              }`}
            >
              <button
                type="button"
                disabled={!interactive}
                onClick={() => toggle(option)}
                className={`w-full text-left ${interactive ? '' : 'cursor-default'}`}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className={`flex-shrink-0 w-4 h-4 border-2 flex items-center justify-center ${
                      allowMultiple ? 'rounded' : 'rounded-full'
                    } ${checked ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300 dark:border-gray-500'}`}
                  >
                    {checked ? (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1 text-sm font-medium text-gray-900 dark:text-white truncate">
                    {option}
                  </span>
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 tabular-nums">
                    {count}
                  </span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </button>
              {revealVoters ? <PollVoters voters={people} directory={directory} /> : null}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-gray-400">
          {voterCount} vote{voterCount === 1 ? '' : 's'}
        </p>
        {!showVoters && !compact && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setDetailsOpen((open) => !open);
            }}
            className="text-[11px] font-semibold text-primary-600 dark:text-primary-400 hover:underline"
          >
            {detailsOpen ? 'Hide details' : 'View more details'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PollVoteBlock;
